// Shared shell bits for the two reading routes (chapter + matter, PD-2): the
// error boundary + suspense skeleton frame, the not-found state, and the
// font-size default. Extracted from reading-view so matter-view doesn't import
// the chapter reader wholesale.

import { Suspense, type ReactNode } from "react";
import { Link } from "react-router";
import { AppNav } from "@/components/app-nav";
import { Skeleton } from "@bindery/ui/components/ui/skeleton";
import { ErrorBoundary } from "@/components/error-boundary";
import { retryCorpusFetch } from "@/lib/corpus";
import { cn } from "@/lib/utils";

// The `/read` route owns its chrome (App.tsx) — the reader hosts `AppNav`
// inline in its own header. These three states have no header to host it in,
// so they hang the floating variant themselves; without it a reader who hit a
// load error would have no theme toggle and no way to their account, which is
// exactly the moment they might want one.

export function ReadingNotFound({ label }: { label: string }) {
  return (
    <>
      <AppNav variant="floating" />
      <main className="mx-auto flex min-h-svh max-w-2xl flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-muted-foreground font-serif">{label}</p>
        <Link
          to="/"
          className="text-primary font-serif underline underline-offset-4"
        >
          Back to the library
        </Link>
      </main>
    </>
  );
}

export function ReadingSkeleton() {
  return (
    <>
      <AppNav variant="floating" />
      <div className="mx-auto max-w-2xl space-y-4 px-6 pt-16">
        <Skeleton className="h-8 w-2/3" />
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton
            key={i}
            className={cn("h-4", i % 3 === 0 ? "w-full" : "w-11/12")}
          />
        ))}
      </div>
    </>
  );
}

/** Error boundary + suspense skeleton around a reading route's loader. */
export function ReadingFrame({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <>
          <AppNav variant="floating" />
          <main className="mx-auto flex min-h-svh max-w-2xl flex-col items-center justify-center gap-4 px-6 text-center">
            <p className="text-foreground font-serif">
              This page could not be loaded.
            </p>
            <p className="text-muted-foreground font-serif text-sm">
              {error.message}
            </p>
            <button
              type="button"
              // Drop the cached REJECTION before re-rendering, or this button
              // would rethrow the same settled promise and change nothing —
              // a corpus fetch is only retried when a reader asks (see
              // `retryCorpusFetch`).
              onClick={() => {
                retryCorpusFetch();
                reset();
              }}
              className="text-primary font-serif underline underline-offset-4"
            >
              Try again
            </button>
          </main>
        </>
      )}
    >
      <Suspense fallback={<ReadingSkeleton />}>{children}</Suspense>
    </ErrorBoundary>
  );
}
